import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiDownload, FiSend, FiPrinter } = FiIcons;

const InvoicePreview = ({ isOpen, onClose, invoice }) => {
  if (!invoice) return null;

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // In a real app, this would generate and download a PDF
    console.log('Download PDF for invoice:', invoice.invoice_number);
  };

  const handleSend = () => {
    // In a real app, this would send the invoice via email
    console.log('Send invoice:', invoice.invoice_number);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-4xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10 print:hidden">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Invoice Preview</h2>
                  <p className="text-sm text-gray-600">{invoice.invoice_number}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrint}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Print Invoice"
                  >
                    <SafeIcon icon={FiPrinter} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Download PDF"
                  >
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleSend}
                    className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200"
                    title="Send Invoice"
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Invoice Content */}
            <div className="p-8 print:p-0">
              {/* Invoice Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">INVOICE</h1>
                  <div className="text-gray-600">
                    <p className="font-semibold text-lg">{invoice.invoice_number}</p>
                    <p>Issue Date: {formatDate(invoice.issue_date)}</p>
                    <p>Due Date: {formatDate(invoice.due_date)}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={FiIcons.FiFileText} className="w-8 h-8 text-white" />
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(invoice.status)} print:hidden`}>
                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* From/To Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">From:</h3>
                  <div className="text-gray-600">
                    <p className="font-semibold text-gray-800">Agency Name</p>
                    <p>123 Business Street</p>
                    <p>City, State 12345</p>
                    <p>Phone: (555) 123-4567</p>
                    <p>Email: billing@agency.com</p>
                    <p>GSTIN: 29ABCDE1234F1Z5</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Bill To:</h3>
                  <div className="text-gray-600">
                    <p className="font-semibold text-gray-800">{invoice.client?.business_name}</p>
                    <p>{invoice.client?.name}</p>
                    {invoice.client?.address && <p>{invoice.client.address}</p>}
                    <p>Phone: {invoice.client?.phone}</p>
                    <p>Email: {invoice.client?.email}</p>
                    {invoice.client?.gstin && <p>GSTIN: {invoice.client.gstin}</p>}
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="mb-8">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Service</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Description</th>
                      <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Qty</th>
                      <th className="border border-gray-300 px-4 py-3 text-right font-semibold">Rate</th>
                      <th className="border border-gray-300 px-4 py-3 text-right font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.services?.map((service, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-3 font-medium">{service.name}</td>
                        <td className="border border-gray-300 px-4 py-3 text-gray-600">{service.description}</td>
                        <td className="border border-gray-300 px-4 py-3 text-center">{service.quantity}</td>
                        <td className="border border-gray-300 px-4 py-3 text-right">{formatCurrency(service.rate)}</td>
                        <td className="border border-gray-300 px-4 py-3 text-right font-medium">{formatCurrency(service.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Ad Spend Section (if applicable) */}
              {invoice.ad_spend > 0 && (
                <div className="mb-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-gray-800 mb-2">Ad Spend Details</h4>
                  <div className="flex justify-between">
                    <span>Client Ad Spend (Direct Cost):</span>
                    <span className="font-medium">{formatCurrency(invoice.ad_spend)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    *Ad spend is billed at cost and is not subject to GST
                  </p>
                </div>
              )}

              {/* Totals Section */}
              <div className="flex justify-end mb-8">
                <div className="w-full max-w-md">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(invoice.subtotal)}</span>
                    </div>
                    
                    {invoice.client?.gst_type !== 'B2C' && invoice.tax_breakdown && (
                      <>
                        {invoice.tax_breakdown.cgst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span>CGST (9%):</span>
                            <span>{formatCurrency(invoice.tax_breakdown.cgst)}</span>
                          </div>
                        )}
                        {invoice.tax_breakdown.sgst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span>SGST (9%):</span>
                            <span>{formatCurrency(invoice.tax_breakdown.sgst)}</span>
                          </div>
                        )}
                        {invoice.tax_breakdown.igst > 0 && (
                          <div className="flex justify-between text-sm">
                            <span>IGST (18%):</span>
                            <span>{formatCurrency(invoice.tax_breakdown.igst)}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t pt-2">
                          <span className="font-medium">Total Tax:</span>
                          <span className="font-medium">{formatCurrency(invoice.tax_breakdown.total_tax)}</span>
                        </div>
                      </>
                    )}
                    
                    {invoice.client?.gst_type === 'B2C' && (
                      <div className="text-sm text-gray-600 italic">
                        No GST applicable (B2C Client)
                      </div>
                    )}
                    
                    <div className="border-t-2 border-gray-300 pt-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total Amount:</span>
                        <span>{formatCurrency(invoice.total_amount)}</span>
                      </div>
                    </div>
                    
                    {invoice.ad_spend > 0 && (
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-lg font-bold text-blue-600">
                          <span>Total with Ad Spend:</span>
                          <span>{formatCurrency(invoice.total_amount + invoice.ad_spend)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Terms & Notes */}
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Payment Terms:</h4>
                  <p className="text-gray-600">{invoice.payment_terms}</p>
                </div>
                
                {invoice.notes && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Notes:</h4>
                    <p className="text-gray-600">{invoice.notes}</p>
                  </div>
                )}
                
                <div className="text-center text-sm text-gray-500 mt-8 pt-8 border-t">
                  <p>Thank you for your business!</p>
                  <p>This is a computer generated invoice.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default InvoicePreview;